import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const DisciplineBoxes = ({ organizationId, user, forceRefresh }) => {
  const [organizationData, setOrganizationData] = useState(null);
  const [disciplineBoxes, setDisciplineBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  console.log('DisciplineBoxes component loaded!', { organizationId, user, forceRefresh });

  useEffect(() => {
    console.log('DisciplineBoxes useEffect triggered', { organizationId, refreshKey });
    if (organizationId) {
      loadData();
    } else {
      console.log('No organization ID provided');
      setLoading(false);
    }
  }, [organizationId, refreshKey]);

  // Refresh when forceRefresh prop changes
  useEffect(() => {
    if (forceRefresh) {
      console.log('Force refresh triggered');
      setRefreshKey(prev => prev + 1);
    }
  }, [forceRefresh]);

  // Add page visibility change event to refresh when returning to tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && organizationId) {
        console.log('Page became visible, refreshing data');
        setRefreshKey(prev => prev + 1);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [organizationId]);

  const loadData = async () => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      
      // Load both organization data and discipline boxes
      const [orgResponse, boxesResponse] = await Promise.all([
        axios.get(`${API_BASE}/api/organizations/${organizationId}`, { headers }),
        axios.get(`${API_BASE}/api/organizations/${organizationId}/discipline-boxes`, { headers })
      ]);
      
      setOrganizationData(orgResponse.data);
      setDisciplineBoxes(boxesResponse.data.boxes || []);
      
      console.log('Loaded org data:', orgResponse.data.shooting_branches);
      console.log('Loaded boxes:', boxesResponse.data.boxes);
      
    } catch (error) {
      console.error('Failed to load data:', error);
      // Fallback to default settings
      setOrganizationData({
        shooting_branches: { nsf: true, dfs: false, dssn: false }
      });
      setDisciplineBoxes([]);
    } finally {
      setLoading(false);
    }
  };

  // Bransjeinformasjon med riktige farger og tekst fra HTML-filen
  const getBranchInfo = (branch) => {
    const branches = {
      nsf: {
        key: 'nsf',
        title: '📋 NSF - Treningsvåpen',
        icon: '📋',
        bgClass: 'bg-blue-900 bg-opacity-20',
        borderClass: 'border-blue-700',
        titleClass: 'text-blue-400',
        textClass: 'text-blue-200',
        content: `
          <div class="space-y-3 text-sm">
            <div>
              <p class="font-medium">Krav for treningsvåpen (NSF):</p>
              <p>• Minst 10 treninger siste 6 måneder</p>
              <p>• Alle typer treningsaktiviteter teller</p>
              <p>• Gjelder pistol, revolver og rifle</p>
              <p>• Dokumentert aktivitet i godkjent skytterklubb</p>
            </div>
          </div>
        `
      },
      dfs: {
        key: 'dfs',
        title: '🎯 DFS - Feltskyting',
        icon: '🎯',
        bgClass: 'bg-purple-900 bg-opacity-20',
        borderClass: 'border-purple-700',
        titleClass: 'text-purple-400',
        textClass: 'text-purple-200',
        content: `
          <div class="space-y-3 text-sm">
            <div>
              <p class="font-medium">Krav for Det Frivillige Skyttervesen (DFS):</p>
              <p>• Være medlem i DFS</p>
              <p>• Gjelder kun rifle</p>
              <p>• Dokumentert aktivitet i godkjent skytterklubb</p>
            </div>
          </div>
        `
      },
      dssn: {
        key: 'dssn',
        title: '⚡ DSSN - Dynamisk',
        icon: '⚡',
        bgClass: 'bg-green-900 bg-opacity-20',
        borderClass: 'border-green-700',
        titleClass: 'text-green-400',
        textClass: 'text-green-200',
        content: `
          <div class="space-y-3 text-sm">
            <div>
              <p class="font-medium">Krav for dynamisk skyting (DSSN):</p>
              <p>• Medlemskap i Sportsskyttere med tilslutning til DSSN</p>
              <p><strong>• Gjennomført sikkerhetskurs</strong></p>
              <p>• Skutt aktivt i minimum 1 år</p>
              <p class="text-green-400"><strong>• Inneha våpen godkjent for en av divisjonene</strong></p>
            </div>
          </div>
        `
      },
      sws: {
        key: 'sws',
        title: '🤠 SWS - Western',
        icon: '🤠',
        bgClass: 'bg-yellow-900 bg-opacity-20',
        borderClass: 'border-yellow-700',
        titleClass: 'text-yellow-400',
        textClass: 'text-yellow-200',
        content: `
          <div class="space-y-3 text-sm">
            <div>
              <p class="font-medium">Scandinavian Western Shooters (SWS):</p>
              <p>• Western skyting</p>
              <p>• Dokumentert aktivitet i godkjent skytterklubb</p>
            </div>
          </div>
        `
      },
      nsu: {
        key: 'nsu',
        title: '🔥 NSU - Svartkrutt',
        icon: '🔥',
        bgClass: 'bg-amber-900 bg-opacity-20',
        borderClass: 'border-amber-500',
        titleClass: 'text-amber-400',
        textClass: 'text-amber-100',
        content: `
          <div class="space-y-3 text-sm">
            <div>
              <p class="font-medium">Norsk Svartkruttunion (NSU):</p>
              <p>• Svartkruttskyting</p>
              <p>• Dokumentert aktivitet i godkjent skytterklubb</p>
            </div>
          </div>
        `
      },
      nrof: {
        key: 'nrof',
        title: '🎖️ NROF - Reserveoffiserer',
        icon: '🎖️',
        bgClass: 'bg-indigo-900 bg-opacity-20',
        borderClass: 'border-indigo-700',
        titleClass: 'text-indigo-400',
        textClass: 'text-indigo-200',
        content: `
          <div class="space-y-3 text-sm">
            <div>
              <p class="font-medium">Norske Reserveoffiserers Forbund (NROF):</p>
              <p>• Reserveoffisersskyting</p>
              <p>• Dokumentert aktivitet i godkjent skytterklubb</p>
            </div>
          </div>
        `
      },
      nmsf: {
        key: 'nmsf',
        title: '🎯 NMSF - Metallsilhuett',
        icon: '🎯',
        bgClass: 'bg-pink-900 bg-opacity-20',
        borderClass: 'border-pink-700',
        titleClass: 'text-pink-400',
        textClass: 'text-pink-200',
        content: `
          <div class="space-y-3 text-sm">
            <div>
              <p class="font-medium">Norges Metallsilhuett Forbund (NMSF):</p>
              <p>• Metallsilhuett-skyting</p>
              <p>• Dokumentert aktivitet i godkjent skytterklubb</p>
            </div>
          </div>
        `
      },
      njff: {
        key: 'njff',
        title: '🦌 NJFF - Jegerskyting',
        icon: '🦌',
        bgClass: 'bg-emerald-900 bg-opacity-20',
        borderClass: 'border-emerald-700',
        titleClass: 'text-emerald-400',
        textClass: 'text-emerald-200',
        content: `
          <div class="space-y-3 text-sm">
            <div>
              <p class="font-medium">Norges Jeger- og Fiskerforbund (NJFF):</p>
              <p>• Jegerskyting med rifle og hagle</p>
              <p>• Dokumentert aktivitet i godkjent skytterklubb</p>
            </div>
          </div>
        `
      },
      nbif: {
        key: 'nbif',
        title: '💼 NBIF - Bedriftsskyting',
        icon: '💼',
        bgClass: 'bg-cyan-900 bg-opacity-20',
        borderClass: 'border-cyan-700',
        titleClass: 'text-cyan-400',
        textClass: 'text-cyan-200',
        content: `
          <div class="space-y-3 text-sm">
            <div>
              <p class="font-medium">Norges bedriftsidrettsforbund (NBIF):</p>
              <p>• Bedriftsskyting med kun rifle</p>
              <p>• Dokumentert aktivitet i godkjent skytterklubb</p>
            </div>
          </div>
        `
      },
      nbsf: {
        key: 'nbsf',
        title: '🪑 NBSF - Benkeskyting',
        icon: '🪑',
        bgClass: 'bg-red-900 bg-opacity-20',
        borderClass: 'border-red-700',
        titleClass: 'text-red-400',
        textClass: 'text-red-200',
        content: `
          <div class="space-y-3 text-sm">
            <div>
              <p class="font-medium">Norges Benkeskytterforbund (NBSF):</p>
              <p>• Benkeskyting med kun rifle</p>
              <p>• Dokumentert aktivitet i godkjent skytterklubb</p>
            </div>
          </div>
        `
      },
      nssf: {
        key: 'nssf',
        title: '⛷️ NSSF - Skiskyting',
        icon: '⛷️',
        bgClass: 'bg-teal-900 bg-opacity-20',
        borderClass: 'border-teal-700',
        titleClass: 'text-teal-400',
        textClass: 'text-teal-200',
        content: `
          <div class="space-y-3 text-sm">
            <div>
              <p class="font-medium">Norges skiskytterforbund (NSSF):</p>
              <p>• Skiskyting med kun rifle</p>
              <p>• Dokumentert aktivitet i godkjent skytterklubb</p>
            </div>
          </div>
        `
      }
    };
    return branches[branch] || null;
  };

  // Debug logging
  console.log('DisciplineBoxes render:', { organizationId, loading, organizationData, user });

  if (!organizationId || loading) {
    return (
      <div className="mt-8 text-center">
        <p className="text-gray-400">Laster disciplinbokser...</p>
      </div>
    );
  }

  // Get active branches and corresponding boxes
  const shootingBranches = organizationData?.shooting_branches || { 
    nsf: true, dfs: false, dssn: false, sws: false, nsu: false, 
    nrof: false, nmsf: false, njff: false, nbif: false, nbsf: false, nssf: false 
  };
  const activeBranches = Object.keys(shootingBranches).filter(branch => shootingBranches[branch]);
  
  // Map branches to box numbers (1-11 for all shooting branches)
  const branchToBoxNumber = { 
    nsf: 1, 
    dfs: 2, 
    dssn: 3,
    sws: 4,
    nsu: 5,
    nrof: 6,
    nmsf: 7,
    njff: 8,
    nbif: 9,
    nbsf: 10,
    nssf: 11
  };
  
  // Filter discipline boxes based on active branches
  const visibleBoxes = disciplineBoxes.filter(box => {
    const branchKey = Object.keys(branchToBoxNumber).find(key => branchToBoxNumber[key] === box.box_number);
    return branchKey && activeBranches.includes(branchKey);
  });

  console.log('Shooting branches:', shootingBranches);
  console.log('Active branches:', activeBranches);
  console.log('Visible boxes:', visibleBoxes);

  if (loading) {
    return (
      <div className="mt-8 text-center">
        <p className="text-gray-400">Laster disciplinbokser...</p>
      </div>
    );
  }

  if (activeBranches.length === 0) {
    return (
      <div className="mt-8 text-center">
        <p className="text-gray-400">Ingen skyttergrener er aktivert. Aktiver dem i Admin → Innstillinger.</p>
      </div>
    );
  }

  if (visibleBoxes.length === 0) {
    return (
      <div className="mt-8 text-center">
        <p className="text-gray-400">Ingen disciplinbokser konfigurert for aktive bransjer.</p>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-white mb-2">Aktive Skyttergrener</h2>
        <p className="text-gray-400 text-sm">Utforsk våre tilgjengelige skyteprogram</p>
      </div>
      
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {visibleBoxes.map((box) => {
          const branchKey = Object.keys(branchToBoxNumber).find(key => branchToBoxNumber[key] === box.box_number);
          const branchInfo = getBranchInfo(branchKey);
          
          return (
            <div
              key={box.box_number}
              className={`${branchInfo.bgClass} border ${branchInfo.borderClass} rounded-lg p-6`}
            >
              <h3 className={`font-semibold ${branchInfo.titleClass} mb-3`}>
                {box.title}
              </h3>
              
              <div 
                className={`${branchInfo.textClass}`}
                dangerouslySetInnerHTML={{ __html: box.content }}
              />
            </div>
          );
        })}
      </div>
      
      <div className="mt-6 text-center">
        <p className="text-gray-500 text-xs">
          Bransjeinnstillinger kan endres av administratorer under Admin → Innstillinger → Skyttergrener
        </p>
      </div>
    </div>
  );
};

export default DisciplineBoxes;